/**
 * The component stack of the error a boundary last caught.
 *
 * A render fault's whole diagnosis is WHICH component threw, and nothing in the
 * app could say. TanStack Router's CatchBoundary builds its errorComponent with
 * `{ error, reset }` and nothing else — its `ErrorComponentProps` type declares
 * an optional `info.componentStack`, but the implementation never passes one —
 * so a route error component is handed the error alone. A "Maximum update depth
 * exceeded" on the strategy report therefore said only that something,
 * somewhere, had looped, and finding it by reading effects did not find it.
 *
 * React 19's `onCaughtError` root option DOES carry the stack, for every
 * boundary in the tree. It fires before the boundary re-renders, so by the time
 * a fallback renders, the stack for that error is already here.
 *
 * Keyed on error IDENTITY, not on recency: a fallback rendering for a different
 * error must show nothing rather than the last stack we happened to see, which
 * would be a confident wrong answer.
 *
 * ONE ERROR THIS CANNOT HELP WITH, and it is the one that prompted it:
 * "Maximum update depth exceeded" is thrown by `getRootForUpdatedFiber` from
 * INSIDE the setState call, not while a component renders, so React has no
 * component stack to give and `componentStack` arrives empty. The card is bare
 * for that one by construction — its JavaScript stack, in the console, names
 * the setter's call site instead. Every other render fault is served here.
 */

let lastError: unknown = null;
let lastStack = '';

export function rememberComponentStack(
  error: unknown,
  componentStack: string | undefined,
): void {
  lastError = error;
  lastStack = componentStack ?? '';
}

export function componentStackFor(error: unknown): string | undefined {
  return error === lastError && lastStack ? lastStack : undefined;
}

/** Test seam — the module remembers one error for the life of the page. */
export function forgetComponentStack(): void {
  lastError = null;
  lastStack = '';
}
